import {
  WSActions,
  WSAlert,
  WSControl,
  WSElement,
  WSForm,
  WSInfoBox,
  WSList,
  WSPanel,
  WSSelect,
  WSText
} from "@wingspanhq/fe-component-library";
import * as Yup from "yup";
import { useEffect, useMemo, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { useQueryCustomerEntity } from "../../../../query/onboarding/queries/useQueryCustomerEntity";
import { WSQueries } from "../../../../query/WSQuery";
import { OnboardingStep } from "../../types";
import {
  getTreatyByCountryCode,
  TreatyArticleType,
  TreatyCountry,
  TreatyProvision,
  TreatyStatus,
  US_TAX_TREATY_COUNTRIES
} from "../../../../shared/constants/taxTreaties";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TreatyBenefitsFormData {
  treatyCountryCode: string;
  provisionIndex: string;
  certifyConditions: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getTreatyOptions(): { value: string; label: string }[] {
  return US_TAX_TREATY_COUNTRIES.filter(
    t =>
      t.status === TreatyStatus.Active ||
      t.status === TreatyStatus.PartiallySuspended
  )
    .sort((a, b) => a.countryName.localeCompare(b.countryName))
    .map(t => ({
      value: t.countryCode,
      label: t.countryName
    }));
}

function getProvisionOptions(
  treaty: TreatyCountry
): { value: string; label: string }[] {
  return treaty.provisions.map((provision, index) => ({
    value: String(index),
    label: `Article ${provision.articleNumber} — ${provision.articleTitle} (${provision.rate === 0 ? "Exempt" : `${provision.rate}%`})`
  }));
}

function getDefaultProvisionIndex(treaty: TreatyCountry): string {
  const idx = treaty.provisions.findIndex(p => p.mostAdvantageous);
  return String(idx >= 0 ? idx : 0);
}

function formatRate(rate: number): string {
  return rate === 0 ? "0% (Exempt)" : `${rate}%`;
}

function formatConditions(provision: TreatyProvision): string[] {
  const items: string[] = [];

  if (provision.requiresNoFixedBase) {
    items.push(
      provision.articleType === TreatyArticleType.BusinessProfits
        ? "You do not have a permanent establishment (PE) in the United States."
        : "You do not have a fixed base regularly available to you in the United States."
    );
  }

  if (provision.maxDaysPresent !== null) {
    const periodLabel =
      provision.dayCountPeriod === "12_month_rolling"
        ? "any 12-month period"
        : provision.dayCountPeriod === "calendar_year"
          ? "the calendar year"
          : "the tax year";
    items.push(
      `You have been present in the United States for no more than ${provision.maxDaysPresent} days during ${periodLabel}.`
    );
  }

  if (provision.maxCompensation !== null) {
    items.push(
      `Your total compensation for services performed in the United States does not exceed $${provision.maxCompensation.toLocaleString()} during the tax year.`
    );
  }

  return items;
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

const validationSchema = Yup.object().shape({
  treatyCountryCode: Yup.string().required(
    "Please select your country of tax treaty residence."
  ),
  provisionIndex: Yup.string().required(),
  certifyConditions: Yup.bool()
    .required()
    .oneOf([true], "You must certify that you meet the treaty conditions.")
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const TreatyBenefits: React.FC<OnboardingStep> = ({ onNext }) => {
  const queryCustomerEntity = useQueryCustomerEntity();
  const treatyOptions = useMemo(getTreatyOptions, []);

  return (
    <WSQueries queries={{ queryCustomerEntity }}>
      {({ queryCustomerEntityData }) => {
        const payeeCountry = queryCustomerEntityData?.country ?? "";
        const initialTreaty = getTreatyByCountryCode(payeeCountry);

        // Country has no treaty at all
        if (!initialTreaty && payeeCountry && payeeCountry !== "US") {
          return (
            <NoTreatyState
              countryCode={payeeCountry}
              onNext={() => onNext?.()}
            />
          );
        }

        const defaultCountryCode =
          initialTreaty &&
          initialTreaty.status !== TreatyStatus.Terminated &&
          initialTreaty.status !== TreatyStatus.Suspended
            ? initialTreaty.countryCode
            : "";

        const defaultProvisionIndex = initialTreaty
          ? getDefaultProvisionIndex(initialTreaty)
          : "0";

        return (
          <WSForm<TreatyBenefitsFormData>
            defaultValues={{
              treatyCountryCode: defaultCountryCode,
              provisionIndex: defaultProvisionIndex,
              certifyConditions: false
            }}
            validationSchema={validationSchema}
          >
            <FormValueResetter />
            <WSList gap="2XL">
              <WSPanel>
                <WSList gap="2XL">
                  <WSElement>
                    <WSText.Paragraph weight="medium" color="gray600" mb="M">
                      Claim tax treaty benefits (W-8BEN Part II)
                    </WSText.Paragraph>
                    <WSText.ParagraphSm weight="book" color="gray600">
                      If you are a tax resident of a country that has an income
                      tax treaty with the United States, you may be eligible for
                      a reduced rate of withholding or an exemption from
                      withholding on certain types of income.
                    </WSText.ParagraphSm>
                  </WSElement>

                  <TreatyCountryField
                    treatyOptions={treatyOptions}
                    defaultCountryCode={defaultCountryCode}
                  />
                </WSList>
              </WSPanel>

              <WSForm.Value name="treatyCountryCode">
                {(selectedCode: string) => {
                  const treaty = getTreatyByCountryCode(selectedCode);

                  if (!selectedCode || !treaty) {
                    return null;
                  }

                  if (
                    treaty.status === TreatyStatus.Terminated ||
                    treaty.status === TreatyStatus.Suspended
                  ) {
                    return (
                      <InactiveTreatyAlert
                        treaty={treaty}
                        onNext={() => onNext?.()}
                      />
                    );
                  }

                  return (
                    <TreatyDetails
                      treaty={treaty}
                      defaultProvisionIndex={
                        treaty.countryCode === defaultCountryCode
                          ? defaultProvisionIndex
                          : getDefaultProvisionIndex(treaty)
                      }
                    />
                  );
                }}
              </WSForm.Value>

              <WSForm.Values
                names={[
                  "treatyCountryCode",
                  "provisionIndex",
                  "certifyConditions"
                ]}
              >
                {(values: Record<string, any>) => {
                  const treaty = getTreatyByCountryCode(
                    values.treatyCountryCode
                  );
                  const isInactive =
                    !treaty ||
                    treaty.status === TreatyStatus.Terminated ||
                    treaty.status === TreatyStatus.Suspended;
                  const isValid =
                    !isInactive && validationSchema.isValidSync(values);

                  return (
                    <WSActions
                      alignment="fill"
                      buttons={[
                        {
                          label: "Continue",
                          disabled: !isValid,
                          onClick: () => onNext?.(),
                          type: "button"
                        }
                      ]}
                    />
                  );
                }}
              </WSForm.Values>

              <WSText.ParagraphXs color="gray400">
                The information on this page is required to determine the
                correct withholding rate for your payments. If you exit or leave
                without completing this step, you will be required to do this
                later.
              </WSText.ParagraphXs>
            </WSList>
          </WSForm>
        );
      }}
    </WSQueries>
  );
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/**
 * Watches `treatyCountryCode` and `provisionIndex` and resets dependent
 * form values when they change, preventing stale certifications.
 * Uses refs to skip the initial render and only react to actual changes.
 */
const FormValueResetter: React.FC = () => {
  const { watch, setValue } = useFormContext<TreatyBenefitsFormData>();
  const treatyCountryCode = watch("treatyCountryCode");
  const provisionIndex = watch("provisionIndex");

  const prevCountryRef = useRef(treatyCountryCode);
  const prevProvisionRef = useRef(provisionIndex);

  // Reset provisionIndex and certifyConditions when country changes
  useEffect(() => {
    if (prevCountryRef.current === treatyCountryCode) return;
    prevCountryRef.current = treatyCountryCode;

    const treaty = getTreatyByCountryCode(treatyCountryCode);
    if (treaty) {
      setValue("provisionIndex", getDefaultProvisionIndex(treaty));
    }
    setValue("certifyConditions", false);
  }, [treatyCountryCode, setValue]);

  // Reset certifyConditions when provision changes
  useEffect(() => {
    if (prevProvisionRef.current === provisionIndex) return;
    prevProvisionRef.current = provisionIndex;

    setValue("certifyConditions", false);
  }, [provisionIndex, setValue]);

  return null;
};

const TreatyCountryField: React.FC<{
  treatyOptions: { value: string; label: string }[];
  defaultCountryCode: string;
}> = ({ treatyOptions, defaultCountryCode }) => (
  <WSForm.Field
    name="treatyCountryCode"
    label="Country of tax treaty residence"
    component={WSSelect}
    componentProps={{
      placeholder: "Select country",
      internalSearch: true,
      options: treatyOptions,
      helperText: defaultCountryCode
        ? "Pre-filled from your account. Change only if your tax treaty residence differs from your country of residence."
        : undefined
    }}
  />
);

const NoTreatyState: React.FC<{
  countryCode: string;
  onNext: () => void;
}> = ({ countryCode, onNext }) => (
  <WSList gap="2XL">
    <WSPanel>
      <WSList gap="2XL">
        <WSElement>
          <WSText.Paragraph weight="medium" color="gray600" mb="M">
            Tax treaty benefits
          </WSText.Paragraph>
          <WSText.ParagraphSm weight="book" color="gray600">
            Based on your country of residence, treaty benefits are not
            available.
          </WSText.ParagraphSm>
        </WSElement>

        <WSAlert theme="warning">
          The United States does not currently have an income tax treaty with
          your country of residence (country code: {countryCode}). The standard
          withholding rate of 30% will apply to your US-source income.
        </WSAlert>
      </WSList>
    </WSPanel>

    <WSActions
      alignment="fill"
      buttons={[
        {
          label: "Continue",
          onClick: onNext
        }
      ]}
    />

    <WSText.ParagraphXs color="gray400">
      If you believe your country of residence is incorrect, go back and update
      your account information.
    </WSText.ParagraphXs>
  </WSList>
);

const InactiveTreatyAlert: React.FC<{
  treaty: TreatyCountry;
  onNext: () => void;
}> = ({ treaty, onNext }) => (
  <WSList gap="M">
    <WSAlert theme="warning">
      The US tax treaty with {treaty.countryName} is currently{" "}
      {treaty.status === TreatyStatus.Terminated ? "terminated" : "suspended"}.
      {treaty.statusNote ? ` ${treaty.statusNote}` : ""} The standard
      withholding rate of 30% will apply.
    </WSAlert>

    <WSActions
      alignment="fill"
      buttons={[
        {
          label: "Continue",
          onClick: onNext,
          type: "button"
        }
      ]}
    />
  </WSList>
);

const TreatyDetails: React.FC<{
  treaty: TreatyCountry;
  defaultProvisionIndex: string;
}> = ({ treaty, defaultProvisionIndex }) => {
  const provisionOptions = useMemo(() => getProvisionOptions(treaty), [treaty]);
  const hasMultipleProvisions = treaty.provisions.length > 1;

  return (
    <WSPanel>
      <WSList gap="2XL">
        <WSElement>
          <WSText.Paragraph weight="medium" color="gray600" mb="M">
            Treaty provision
          </WSText.Paragraph>
          <WSText.ParagraphSm weight="book" color="gray600">
            {hasMultipleProvisions
              ? `The US-${treaty.countryName} tax treaty has multiple provisions that may apply to your income. The most advantageous option has been selected as the default.`
              : `The US-${treaty.countryName} tax treaty provides the following provision for contractor income.`}
          </WSText.ParagraphSm>
        </WSElement>

        {treaty.statusNote && (
          <WSAlert theme="info">{treaty.statusNote}</WSAlert>
        )}

        {hasMultipleProvisions ? (
          <WSForm.Field
            name="provisionIndex"
            label="Applicable treaty article"
            component={WSSelect}
            componentProps={{
              options: provisionOptions
            }}
          />
        ) : (
          <WSInfoBox>
            <WSText.ParagraphSm weight="medium">
              Article {treaty.provisions[0].articleNumber} —{" "}
              {treaty.provisions[0].articleTitle}
            </WSText.ParagraphSm>
            <WSText.ParagraphSm mt="XS" color="gray500">
              Withholding rate: {formatRate(treaty.provisions[0].rate)}
            </WSText.ParagraphSm>
          </WSInfoBox>
        )}

        <WSForm.Value name="provisionIndex">
          {(selectedIndex: string) => {
            const idx =
              selectedIndex != null
                ? Number(selectedIndex)
                : Number(defaultProvisionIndex);
            const provision = treaty.provisions[idx] ?? treaty.provisions[0];
            if (!provision) return null;

            const conditions = formatConditions(provision);

            return (
              <WSList gap="M">
                {hasMultipleProvisions && (
                  <WSInfoBox>
                    <WSText.ParagraphSm weight="medium">
                      Withholding rate: {formatRate(provision.rate)}
                    </WSText.ParagraphSm>
                  </WSInfoBox>
                )}

                <WSElement>
                  <WSText.ParagraphSm weight="medium" color="gray600" mb="XS">
                    Conditions for treaty benefit
                  </WSText.ParagraphSm>
                  <WSText.ParagraphSm weight="book" color="gray500" mb="M">
                    {provision.conditions}
                  </WSText.ParagraphSm>
                </WSElement>

                {conditions.length > 0 && (
                  <WSInfoBox>
                    <WSText.ParagraphSm weight="medium" mb="XS">
                      By checking the box below, you certify that:
                    </WSText.ParagraphSm>
                    <ul>
                      {conditions.map((condition, i) => (
                        <li key={i}>
                          <WSText.ParagraphSm>{condition}</WSText.ParagraphSm>
                        </li>
                      ))}
                    </ul>
                  </WSInfoBox>
                )}

                <WSForm.Field
                  name="certifyConditions"
                  component={WSControl}
                  componentProps={{
                    type: "checkbox",
                    label: `I certify that I am a resident of ${treaty.countryName} within the meaning of the US-${treaty.countryName} income tax treaty, and I meet all conditions required to claim benefits under Article ${provision.articleNumber} (${provision.articleTitle}).`,
                    size: "S"
                  }}
                />
              </WSList>
            );
          }}
        </WSForm.Value>
      </WSList>
    </WSPanel>
  );
};
